import React, { useState, useEffect } from "react";
import { db } from "../firebase";
import { collection, getDocs, query, where } from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend
);

const Dashboard = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalAgents: 0,
    verifiedAgents: 0,
    pendingAgents: 0,
    rejectedAgents: 0,
    averageReputation: 0,
    recentRegistrations: 0,
  });
  const navigate = useNavigate();
  const {
    user,
    isAdmin,
    isModerator,
    userRole,
    loading: authLoading,
  } = useAuth();

  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        if (!user) {
          setAgents([]);
          return;
        }

        const querySnapshot = await getDocs(collection(db, "agents"));
        let agentsList = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Filter agents based on user role
        if (userRole === "User") {
          // Regular users can only see their own agents
          agentsList = agentsList.filter((agent) => agent.userId === user.uid);
        }
        // Admins and Moderators can see all agents (no filtering)
        setAgents(agentsList);

        // Calculate statistics
        const totalAgents = agentsList.length;
        const verifiedAgents = agentsList.filter(
          (agent) =>
            agent["Verification Status"] &&
            agent["Verification Status"].toLowerCase().includes("verified")
        ).length;
        const pendingAgents = agentsList.filter(
          (agent) =>
            agent["Verification Status"] &&
            agent["Verification Status"].toLowerCase().includes("pending")
        ).length;
        const unverifiedAgents = agentsList.filter(
          (agent) =>
            agent["Verification Status"] &&
            agent["Verification Status"].toLowerCase().includes("unverified")
        ).length;
        const underReviewAgents = agentsList.filter(
          (agent) =>
            agent["Verification Status"] &&
            agent["Verification Status"].toLowerCase().includes("under review")
        ).length;

        const reputationScores = agentsList
          .map((agent) => parseFloat(agent["Reputation Score"]) || 0)
          .filter((score) => score > 0);
        const averageReputation =
          reputationScores.length > 0
            ? (
                reputationScores.reduce((sum, score) => sum + score, 0) /
                reputationScores.length
              ).toFixed(1)
            : 0;

        // Calculate recent registrations (last 7 days)
        const sevenDaysAgo = new Date();
        sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
        const recentRegistrations = agentsList.filter((agent) => {
          const creationDate = agent["Creation Date"];
          if (!creationDate) return false;
          const agentDate = new Date(creationDate);
          return agentDate >= sevenDaysAgo;
        }).length;

        setStats({
          totalAgents,
          verifiedAgents,
          pendingAgents,
          unverifiedAgents,
          underReviewAgents,
          averageReputation,
          recentRegistrations,
        });
      } catch (e) {
        console.error("Error fetching dashboard data:", e);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [user, userRole, authLoading]);

  const StatCard = ({ title, value, icon, color, subtitle }) => (
    <div className="valia-card p-6 group cursor-pointer">
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-valia-gray-600 text-sm font-medium group-hover:text-valia-gray-700 transition-colors duration-200 truncate">
            {title}
          </p>
          <p className="text-2xl font-bold text-valia-gray-900 mt-1 group-hover:text-valia-primary transition-colors duration-200">
            {value}
          </p>
          {subtitle && (
            <p className="text-valia-gray-500 text-xs mt-1 group-hover:text-valia-gray-600 transition-colors duration-200 truncate">
              {subtitle}
            </p>
          )}
        </div>
        <div
          className={`p-3 rounded-full ${color} group-hover:scale-110 group-hover:shadow-lg transition-all duration-300 ease-in-out flex-shrink-0 ml-3`}
        >
          {icon}
        </div>
      </div>
    </div>
  );

  if (loading || authLoading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-center">
          <div className="valia-spinner h-12 w-12 mx-auto mb-4"></div>
          <span className="text-valia-gray-600 text-lg">
            Loading dashboard...
          </span>
        </div>
      </div>
    );
  }

  const verificationPercentage =
    stats.totalAgents > 0
      ? (stats.verifiedAgents / stats.totalAgents) * 100
      : 0;
  const pendingPercentage =
    stats.totalAgents > 0 ? (stats.pendingAgents / stats.totalAgents) * 100 : 0;
  const unverifiedPercentage =
    stats.totalAgents > 0
      ? (stats.unverifiedAgents / stats.totalAgents) * 100
      : 0;
  const underReviewPercentage =
    stats.totalAgents > 0
      ? (stats.underReviewAgents / stats.totalAgents) * 100
      : 0;

  return (
    <div>
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-valia-primary mb-2">
          Dashboard
        </h1>
        <p className="text-valia-gray-600 text-base">
          {userRole === "User"
            ? "Monitor and manage your registered agents"
            : userRole === "Moderator"
            ? "Review and manage all agents in the system"
            : "Monitor and manage the entire agent ecosystem"}
        </p>
      </div>

      {/* Stats Overview */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Agents"
          value={stats.totalAgents}
          icon={
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
              />
            </svg>
          }
          color="bg-valia-primary"
          subtitle="Registered agents"
        />

        <StatCard
          title="Verified Agents"
          value={stats.verifiedAgents}
          icon={
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          }
          color="bg-valia-accent"
          subtitle={`${verificationPercentage.toFixed(1)}% verified`}
        />

        <StatCard
          title="Average Reputation"
          value={stats.averageReputation}
          icon={
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"
              />
            </svg>
          }
          color="bg-valia-warning"
          subtitle="Trust score"
        />

        <StatCard
          title="Recent Registrations"
          value={stats.recentRegistrations}
          icon={
            <svg
              className="w-6 h-6 text-white"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
          }
          color="bg-valia-secondary"
          subtitle="Last 7 days"
        />
      </div>

      {/* Verification Status with Chart */}
      <div className="grid grid-cols-1 xl:grid-cols-2 gap-6 mb-8">
        <div className="valia-card p-6 group">
          <h3 className="text-lg font-semibold text-valia-gray-900 mb-4 group-hover:text-valia-primary transition-colors duration-200">
            Verification Status
          </h3>

          {/* Verification Status Bar Chart */}
          <div className="h-40 sm:h-48">
            <Bar
              data={{
                labels: ["Verified", "Pending", "Under Review", "Unverified"],
                datasets: [
                  {
                    label: "Number of Agents",
                    data: [
                      stats.verifiedAgents,
                      stats.pendingAgents,
                      stats.underReviewAgents,
                      stats.unverifiedAgents,
                    ],
                    backgroundColor: [
                      "#4CAF50", // valia-accent
                      "#FF9800", // valia-warning
                      "#1A2B3C", // valia-primary
                      "#F44336", // valia-error
                    ],
                    borderColor: [
                      "#66BB6A", // valia-accent-light
                      "#FFB74D", // warning-light
                      "#2A3B4C", // valia-primary-light
                      "#E57373", // error-light
                    ],
                    borderWidth: 1,
                  },
                ],
              }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: false,
                  },
                  tooltip: {
                    backgroundColor: "#F9FAFB", // valia-gray-50
                    titleColor: "#111827", // valia-gray-900
                    bodyColor: "#374151", // valia-gray-700
                    borderColor: "#E5E7EB", // valia-gray-200
                    borderWidth: 1,
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    position: "left",
                    ticks: {
                      color: "#6B7280", // valia-gray-500
                      font: {
                        size: 11,
                      },
                    },
                    grid: {
                      color: "#E5E7EB", // valia-gray-200
                    },
                  },
                  x: {
                    ticks: {
                      color: "#6B7280", // valia-gray-500
                      font: {
                        size: 11,
                      },
                    },
                    grid: {
                      display: false,
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Agent Analytics */}
        <div className="valia-card p-6 group">
          <h3 className="text-lg font-semibold text-valia-gray-900 mb-4 flex items-center group-hover:text-valia-primary transition-colors duration-200">
            <svg
              className="w-5 h-5 mr-2 text-valia-primary group-hover:text-valia-primary-dark group-hover:scale-110 transition-all duration-200"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z"
              />
            </svg>
            Agent Analytics
          </h3>

          <div className="space-y-4">
            {/* Agent Type Distribution */}
            <div className="bg-valia-gray-50 rounded-valia p-4 border border-valia-gray-200 hover:border-valia-gray-300 hover:bg-valia-gray-100 transition-all duration-200 ease-in-out">
              <h4 className="text-sm font-medium text-valia-gray-700 mb-3 group-hover:text-valia-gray-800 transition-colors duration-200">
                Agent Types
              </h4>
              <div className="space-y-2">
                {(() => {
                  const types = agents.reduce((acc, agent) => {
                    const type = agent["Agent Type"] || "Unknown";
                    acc[type] = (acc[type] || 0) + 1;
                    return acc;
                  }, {});

                  return Object.entries(types).map(([type, count]) => (
                    <div
                      key={type}
                      className="flex items-center justify-between"
                    >
                      <span className="text-valia-gray-600 text-sm truncate flex-1 mr-2">
                        {type}
                      </span>
                      <div className="flex items-center space-x-2 flex-shrink-0">
                        <div className="w-16 bg-valia-gray-200 rounded-full h-2">
                          <div
                            className="bg-valia-primary h-2 rounded-full"
                            style={{
                              width: `${(count / stats.totalAgents) * 100}%`,
                            }}
                          ></div>
                        </div>
                        <span className="text-valia-gray-900 text-sm font-medium">
                          {count}
                        </span>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            </div>

            {/* Performance Metrics */}
            <div className="bg-valia-gray-50 rounded-valia p-4 border border-valia-gray-200 hover:border-valia-gray-300 hover:bg-valia-gray-100 transition-all duration-200 ease-in-out">
              <h4 className="text-sm font-medium text-valia-gray-700 mb-3 group-hover:text-valia-gray-800 transition-colors duration-200">
                Performance Metrics
              </h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-valia-success">
                    {verificationPercentage.toFixed(1)}%
                  </div>
                  <div className="text-xs text-valia-gray-600">
                    Success Rate
                  </div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-valia-primary">
                    {stats.averageReputation}
                  </div>
                  <div className="text-xs text-valia-gray-600">
                    Avg Reputation
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Agents */}
      <div className="valia-card group">
        <div className="valia-card-header group-hover:border-valia-gray-300 transition-colors duration-200">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-valia-gray-900 group-hover:text-valia-primary transition-colors duration-200">
              Recent Agents
            </h3>
            <button
              onClick={() => navigate("/agents")}
              className="valia-btn-primary valia-btn-sm"
            >
              View All →
            </button>
          </div>
        </div>
        <div className="valia-card-body">
          {agents.length === 0 ? (
            <div className="text-center py-8">
              <svg
                className="w-12 h-12 text-valia-gray-400 mx-auto mb-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth="2"
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
              <p className="text-valia-gray-600 text-lg mb-4">
                No agents registered yet
              </p>
              {userRole !== "Moderator" && (
                <button
                  onClick={() => navigate("/register-agent")}
                  className="valia-btn-primary valia-btn-sm"
                >
                  Register Your First Agent
                </button>
              )}
            </div>
          ) : (
            <div className="space-y-4">
              {agents.slice(0, 5).map((agent) => (
                <div
                  key={agent.id}
                  className="flex flex-col sm:flex-row sm:items-center justify-between p-4 bg-valia-gray-50 rounded-valia border border-valia-gray-200 hover:border-valia-gray-300 hover:bg-valia-gray-100 hover:shadow-sm transition-all duration-200 ease-in-out cursor-pointer group"
                  onClick={() => navigate(`/agents/${agent.id}`)}
                >
                  <div className="flex items-center space-x-4 mb-2 sm:mb-0">
                    <div className="w-10 h-10 bg-valia-primary rounded-full flex items-center justify-center group-hover:bg-valia-primary-dark group-hover:scale-110 transition-all duration-200 ease-in-out flex-shrink-0">
                      <svg
                        className="w-5 h-5 text-white group-hover:text-white transition-colors duration-200"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth="2"
                          d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-valia-gray-900 font-medium group-hover:text-valia-primary transition-colors duration-200 text-base truncate">
                        {agent["Agent Name"] || "Unnamed Agent"}
                      </h4>
                      <p className="text-valia-gray-600 text-sm group-hover:text-valia-gray-700 transition-colors duration-200 truncate">
                        {agent["Agent Type"] || "Unknown Type"}
                      </p>
                    </div>
                  </div>
                  <div className="flex items-center justify-between sm:space-x-4">
                    <div className="text-left sm:text-right">
                      <p className="text-valia-gray-700 text-sm group-hover:text-valia-gray-800 transition-colors duration-200">
                        Reputation: {agent["Reputation Score"] || "N/A"}
                      </p>
                      <p className="text-valia-gray-500 text-xs group-hover:text-valia-gray-600 transition-colors duration-200">
                        {agent["Creation Date"] || "Unknown Date"}
                      </p>
                    </div>
                    <div
                      className={`px-3 py-1 rounded-full text-xs font-medium group-hover:scale-105 transition-all duration-200 ease-in-out flex-shrink-0 ${
                        agent["Verification Status"] === "Verified"
                          ? "valia-badge-success"
                          : agent["Verification Status"] === "Pending"
                          ? "valia-badge-warning"
                          : agent["Verification Status"] === "Under Review"
                          ? "valia-badge-info"
                          : agent["Verification Status"] === "Unverified"
                          ? "valia-badge-error"
                          : "valia-badge-gray"
                      }`}
                    >
                      {agent["Verification Status"] || "Unknown"}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
